/**
 * Theme runtime — the pure half of Scrivar's dark mode.
 *
 * Three user-visible choices: `system` (follow the OS), `light`, `dark`.
 * The resolved state is expressed as a single `dark` class on
 * <html>, which Tailwind's `darkMode: 'class'` strategy keys off.
 *
 * Everything here is framework-free and side-effect-free apart from the two
 * functions that deliberately touch storage / the document, so it can be
 * unit-tested directly and reused by the inline boot script's contract.
 *
 * NOTE: the no-flash boot script in `web/index.html` duplicates this logic in
 * minified inline form on purpose — it must run before first paint, which
 * rules out importing a module. Keep the two in sync: same key, same
 * precedence (explicit dark → dark; explicit light → light; anything else →
 * the OS preference).
 */

/** The three selectable themes, in display order. */
export const THEMES = ['system', 'light', 'dark'];

/** localStorage key. Mirrored verbatim by the index.html boot script. */
export const THEME_STORAGE_KEY = 'scrivar_theme';

const DEFAULT_THEME = 'system';
const DARK_QUERY = '(prefers-color-scheme: dark)';

/**
 * Read the persisted theme.
 * Anything not in THEMES — a stale value, a hand-edited key, a blocked
 * localStorage — resolves to 'system'.
 * @returns {'system'|'light'|'dark'}
 */
export function getStoredTheme() {
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    return THEMES.includes(raw) ? raw : DEFAULT_THEME;
  } catch {
    // Private mode / disabled storage: fall back to following the OS.
    return DEFAULT_THEME;
  }
}

/**
 * Persist a theme choice, normalising anything unrecognised to 'system'.
 * Never throws — storage failures degrade to an in-memory-only choice.
 * @param {string} theme
 * @returns {'system'|'light'|'dark'} the normalised value that was stored
 */
export function storeTheme(theme) {
  const next = THEMES.includes(theme) ? theme : DEFAULT_THEME;
  try {
    localStorage.setItem(THEME_STORAGE_KEY, next);
  } catch {
    // Quota / blocked storage — the session still honours the choice.
  }
  return next;
}

/**
 * Resolve a theme choice plus the current OS preference into "is it dark?".
 * @param {string} theme one of THEMES (anything else is treated as 'system')
 * @param {boolean} systemDark whether the OS currently prefers dark
 * @returns {boolean}
 */
export function resolveIsDark(theme, systemDark) {
  if (theme === 'dark') return true;
  if (theme === 'light') return false;
  return Boolean(systemDark);
}

/**
 * Toggle the `dark` class on <html>. The single place the DOM learns about
 * the theme; every other class on the element is left untouched.
 * @param {boolean} isDark
 */
export function applyThemeClass(isDark) {
  if (typeof document === 'undefined' || !document.documentElement) return;
  document.documentElement.classList.toggle('dark', Boolean(isDark));
}

/**
 * Current OS preference. Guarded for environments without matchMedia
 * (node-env tests, very old shells) — those report "not dark".
 * @returns {boolean}
 */
export function prefersDark() {
  try {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return Boolean(window.matchMedia(DARK_QUERY).matches);
  } catch {
    return false;
  }
}

/**
 * The MediaQueryList the provider subscribes to while theme === 'system'.
 * Returns null when matchMedia is unavailable so callers can skip cleanly.
 * @returns {MediaQueryList|null}
 */
export function getDarkMediaQuery() {
  try {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null;
    return window.matchMedia(DARK_QUERY);
  } catch {
    return null;
  }
}
